import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Translation, TranslocoLoader } from "@jsverse/transloco";
import { Observable, catchError, of } from "rxjs";

import { DEFAULT_LANGUAGE } from "./i18n.tokens";
import { i18nAssetUrl } from "./i18n-asset-url";

// Relative path so the app keeps working under a non-root <base href>. `lang` is the scope
// path for a lazy scope (`platform/en`), so one loader covers the root bundle and every scope.
@Injectable({ providedIn: "root" })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  /**
   * Loads one bundle, falling back to the English file for the same scope when it is absent.
   *
   * The portal ships thirty languages and eighty-five lazy scopes; almost none of those files
   * exist, and that is the intended state — a language is translated scope by scope, chrome
   * first. Transloco's own missing-key fallback only helps once a bundle has loaded, so a
   * scope file that 404s leaves every key on the screen rendering as its raw literal
   * (`manageCatalog.editor.choose` on a label). Substituting the scope's English file turns
   * that into an English screen inside a translated shell, which is the honest partial state
   * and the one the coverage manifest reports.
   *
   * An empty object, not an error, when even English is missing: a scope whose file has not
   * been created must not take the app down.
   */
  getTranslation(lang: string): Observable<Translation> {
    return this.fetch(lang).pipe(
      catchError(() => {
        const fallback = toFallbackPath(lang);
        return fallback === null ? of<Translation>({}) : this.fetch(fallback).pipe(catchError(() => of<Translation>({})));
      })
    );
  }

  private fetch(path: string): Observable<Translation> {
    return this.http.get<Translation>(i18nAssetUrl(`assets/i18n/${path}.json`));
  }
}

/** `shell/hi` → `shell/en`, `hi` → `en`, and null for anything already English. */
function toFallbackPath(lang: string): string | null {
  const cut = lang.lastIndexOf("/");
  const scope = cut === -1 ? "" : lang.slice(0, cut + 1);
  const code = cut === -1 ? lang : lang.slice(cut + 1);
  return code === DEFAULT_LANGUAGE ? null : `${scope}${DEFAULT_LANGUAGE}`;
}
